import { useState, useEffect } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { MediaCollection } from "api/media-library";
import { cn } from "@/lib/utils";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";

interface Props {
  collections: Array<MediaCollection>;
  selectedCollection: MediaCollection | null;
  className?: string;
  disabled?: boolean;
  onChange(value: string | string[]): void;
}

export function CollectionSelectorModern(props: Props) {
  const [open, setOpen] = useState(false);
  const [currentSelectedCollection, setCurrentSelectedCollection] = useState(
    props.selectedCollection?._id || null
  );

  useEffect(() => {
    if (props.selectedCollection?._id !== currentSelectedCollection) {
      setCurrentSelectedCollection(props.selectedCollection?._id ?? null);
    }
  }, [props.selectedCollection?._id]);

  const handleChange = (collectionId: string) => {
    setCurrentSelectedCollection(collectionId);
    props.onChange(collectionId);
    setOpen(false);
  };

  const selectedCollection = props.collections.find(
    (c) => c._id === currentSelectedCollection
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-8 w-full md:w-[240px] justify-between",
            "font-inter text-sm font-medium",
            dropdownTriggerClassName,
            props.className
          )}
          disabled={props.disabled}
        >
          {selectedCollection ? (
            <div className="flex items-center gap-2">
              <div
                className="h-2 w-2 rounded-full shrink-0"
                style={{
                  background: selectedCollection.preferences?.color || "#ccc",
                }}
              />
              <span className="truncate">{selectedCollection.name}</span>
            </div>
          ) : (
            <span className="text-neutral-500">Choose collection...</span>
          )}
          <i
            className={cn(
              "fa-solid fa-chevron-down ml-2 h-4 w-4 shrink-0",
              dropdownChevronClassName
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" disableAnimation>
        <Command>
          <CommandInput
            placeholder="Search collections..."
            className="border-neutral-200 h-9"
          />
          <CommandList>
            <CommandEmpty>No collections found.</CommandEmpty>
            <CommandGroup>
              {props.collections.map((collection) => (
                <CommandItem
                  key={collection._id}
                  value={collection.name}
                  onSelect={() => handleChange(collection._id)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{
                        background: collection.preferences?.color || "#ccc",
                      }}
                    />
                    <span className="truncate">{collection.name}</span>
                  </div>
                  <i
                    className={cn(
                      "fa-solid fa-check ml-auto h-4 w-4",
                      currentSelectedCollection === collection._id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
